import { useMemo } from 'react'
import { type UserGroupType } from 'types/accounts'
import { Profile } from 'types/types'

export const RoleGroupMatchTypes = {
  SOME: 'some',
  EVERY: 'every',
  EXACT: 'exact',
} as const

type RoleGroupMatchType = (typeof RoleGroupMatchTypes)[keyof typeof RoleGroupMatchTypes]

/**
 * Hook to check if a user profile has specific role groups
 *
 * @param profile - The user profile object containing roleGroups
 * @param groups - Single array of groups or array of group arrays (for OR logic with exact matching)
 * @param matchType - Matching strategy: 'some' (default), 'every', or 'exact'
 *
 * @example
 * // Check if user has ANY of these roles
 * const canView = useHasRoleGroups(profile, [UserGroups.Admin, UserGroups.User])
 *
 * @example
 * // Check if user has ALL of these roles
 * const canManage = useHasRoleGroups(profile, [UserGroups.Admin, UserGroups.Manager], RoleGroupMatchTypes.EVERY)
 *
 * @example
 * // Check if user has EXACTLY these roles (and no others)
 * const isComplianceOnly = useHasRoleGroups(profile, [UserGroups.Compliance], RoleGroupMatchTypes.EXACT)
 *
 * @example
 * // Check if user has EXACTLY any of these role combinations
 * const isComplianceView = useHasRoleGroups(profile, [
 *   [UserGroups.Compliance],
 *   [UserGroups.Compliance, UserGroups.Login]
 * ], RoleGroupMatchTypes.EXACT)
 */
export const useHasRoleGroups = (
  profile: Profile,
  groups: UserGroupType[] | UserGroupType[][],
  matchType: RoleGroupMatchType = RoleGroupMatchTypes.SOME
): boolean => {
  const { roleGroups } = profile || {}

  const hasRoleGroup = useMemo(() => {
    if (!roleGroups) return false

    // Handle array of arrays (OR logic for exact matching)
    if (!!groups.length && Array.isArray(groups[0])) {
      const groupCombinations = groups as UserGroupType[][]

      return groupCombinations.some(groupCombo => {
        if (matchType === RoleGroupMatchTypes.EXACT) {
          return (
            roleGroups.length === groupCombo.length &&
            groupCombo.every(group => roleGroups.includes(group))
          )
        }

        if (matchType === RoleGroupMatchTypes.EVERY) {
          return groupCombo.every(group => roleGroups.includes(group))
        }

        // default to some
        return groupCombo.some(group => roleGroups.includes(group))
      })
    }

    // Handle single array
    const singleGroups = groups as UserGroupType[]

    if (matchType === RoleGroupMatchTypes.EXACT) {
      return (
        roleGroups.length === singleGroups.length &&
        singleGroups.every(group => roleGroups.includes(group))
      )
    }

    if (matchType === RoleGroupMatchTypes.EVERY) {
      return singleGroups.every(group => roleGroups.includes(group))
    }

    // default to some
    return singleGroups.some(group => roleGroups.includes(group))
  }, [roleGroups, groups, matchType])

  return hasRoleGroup
}
